/**
 * @description Creates a form label and container around a custom field component (similar to InputLabel, only the
 *  label is always placed at the top of the container)
 */

import React from 'react';
import Box from '@mui/material/Box';
import { PropTypes } from 'prop-types';
import { useFormControl } from '@mui/material/FormControl';

/**
 * @prop children {React.Element | React.Element[]}: react component to be wrapped
 * @prop label {string}: label to be rendred at the top of the container
 * @prop p {string | number}: padding in the inside of the container
 */
const CustomFormLabel = ({
  children,
  label,
  p
}) => {
  const { required, error } = useFormControl();

  let borderColor = 'grey.400';
  let textColor = 'text.secondary';

  // If the form control context is marked as error, set the border color and text to error styles
  if (error) {
    borderColor = 'error.main';
    textColor = 'error.main';
  }

  const styles = {
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      border: 1,
      borderTop: 0,
      borderColor,
      borderRadius: 1
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      zIndex: 1
    },
    headerBorderBefore: {
      borderTop: 1,
      borderColor,
      width: '0.6em',
      borderTopLeftRadius: 1
    },
    headerLabel: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: '0.25em',
      width: 'fit-content',
      height: '2em',
      margin: '-1em 0.5em 0 0.5em',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '0.75em',
      lineHeight: '1em',
      fontWeight: 600
    },
    headerBorderAfter: {
      borderTop: 1,
      borderColor,
      width: '1em',
      flexGrow: 2
    },
    label: {
      color: textColor
    },
    required: {
      color: textColor
    },
    childrenContainer: {
      p,
      marginTop: '-0.75em'
    }
  };

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.header}>
        <Box sx={styles.headerBorderBefore} />
        <Box sx={styles.headerLabel}>
          <Box component='span' sx={styles.label}>{label}</Box>
          {required && (
            <Box component='span' sx={styles.required}>*</Box>
          )}
        </Box>
        <Box sx={styles.headerBorderAfter} />
      </Box>
      <Box sx={styles.childrenContainer}>
        {children}
      </Box>
    </Box>
  );
}

CustomFormLabel.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  label: PropTypes.string.isRequired,
  p: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

CustomFormLabel.defaultProps = {
  p: '1em'
};

export default CustomFormLabel;
